import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  withStyles,
  Avatar,
  Button,
  Typography,
  TextareaAutosize,
  IconButton,
  Paper,
} from "@material-ui/core";
import { ThumbUp, Delete, Edit } from "@material-ui/icons";
import { Link } from "react-router-dom";
import {
  unlikePost,
  likePost,
  deletePost,
  updatePost,
} from "../../actions/postActions";

const styles = (theme) => ({
  firstContainer: {
    display: "flex",
    alignItems: "center",
  },

  paper: {
    padding: theme.spacing(2),
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    border: "1px solid #e0e0e0",
    borderRadius: "10px",
  },

  avatar: {
    width: theme.spacing(7),
    height: theme.spacing(7),
    marginRight: theme.spacing(1.5),
  },

  username: {
    fontWeight: "600",
    color: theme.palette.primary.main,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },

  time: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    marginTop: theme.spacing(1),
  },

  actionButtons: {
    marginTop: theme.spacing(1.5),
  },
  
  textarea: {
    width: "100%",
    padding: theme.spacing(1),
    borderRadius: "5px",
    border: "1px solid #e0e0e0",
    marginBottom: theme.spacing(1),
    resize: "none",
    backgroundColor: theme.palette.type === 'dark' ? '#333' : '#fff',
    color: theme.palette.type === 'dark' ? '#fff' : '#000',
  },
});

const Post = ({ classes, post }) => {
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(post.text);
  const store = useSelector((state) => state);
  const { auth, error } = store;

  const userId = auth?.user?._id;

  const handleUpdatePost = () => {
    dispatch(updatePost(post._id, editedText));
    setIsEditing(false);
  };

  const handleLike = () => {
    if (post.likes.some((like) => like.user === userId)) {
      dispatch(unlikePost(post._id));
    } else {
      dispatch(likePost(post._id));
    }
  };

  const handleDelete = () => {
    dispatch(deletePost(post._id));
  };

  return (
    <Paper className={classes.paper}>
      <div className={classes.firstContainer}>
        <Avatar
          className={classes.avatar}
          src={post.user.profilePic || ""}
          alt={post.user.login}
        />
        <Link to={`/profile/${post.user.id}`} className={classes.username}>
          {post.user.login}
        </Link>
      </div>

      <Typography variant="body1" paragraph>
        {post.text}
      </Typography>

      <Typography variant="caption" className={classes.time}>
        {new Date(post.createdAt).toLocaleString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })}
        {post.edited && <span> (Edited)</span>}
      </Typography>

      <div className={classes.actionButtons}>
        <IconButton
          onClick={handleLike}
          color={
            post.likes.some((like) => like.user === userId)
              ? "primary"
              : "default"
          }
        >
          <ThumbUp />
        </IconButton>
        {post.likes.length}

        {isEditing ? (
          <div>
            <TextareaAutosize
              rowsMin={3}
              placeholder="Edit your post"
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
              className={classes.textarea}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpdatePost}
            >
              Save
            </Button>
            <Button onClick={() => setIsEditing(false)}>Cancel</Button>
          </div>
        ) : (
          <>
            {userId === post.user.id && (
              <IconButton onClick={() => setIsEditing(true)}>
                <Edit />
              </IconButton>
            )}
          </>
        )}

        {userId === post.user.id && (
          <IconButton onClick={handleDelete}>
            <Delete />
          </IconButton>
        )}
      </div>

      {error && <p>{error}</p>}
    </Paper>
  );
};

export default withStyles(styles)(Post);
